import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';
import apiService from "../../utils/apiService";
import { useAuth } from "../../contexts/AuthContext";
import { Icon } from 'react-icons-kit';
import { eyeOff } from 'react-icons-kit/feather/eyeOff';
import { eye } from 'react-icons-kit/feather/eye'


function LoginModal({ onClose, loginType }) {
  const { login } = useAuth();
  const [view, setView] = useState("login");
  const [type, setType] = useState('password');
  const [confirmType, setConfirmType] = useState('password');
  const [icon, setIcon] = useState(eyeOff);
  const [confirmIcon, setConfirmIcon] = useState(eyeOff);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    otp: "",
    newPassword: "",
    enterpriseName: "",
    enterpriseDescription: "",
    enterpriseLocation: "",
    enterpriseWebsite: "",
    enterpriseContact: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { userData, setLoginType } = useAuth();
  const navigate = useNavigate();

  const setErrorMessage = (message) => {
    setError(message);
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoading(false);

    let updatedValue = value;

    // Automatically enforce "https://" for enterpriseWebsite field
    // if (name === "enterpriseWebsite" && value) {
    //   if (value.startsWith("http://")) {
    //     updatedValue = value.replace("http://", "https://"); // Replace http with https
    //   } else if (!value.startsWith("https://")) {
    //     updatedValue = `https://${value}`; // Prepend https if missing
    //   }
    // }
    if (name === "email") {
      updatedValue = value.toLowerCase();
    }
    setFormData({ ...formData, [name]: updatedValue });
  };


  // Define handleError function
  const handleError = (message) => {
    setError(message);
    setLoading(false);
  };

  // Validation functions
  const validatePasswordMatch = () => {
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return false;
    }
    return true;
  };

  const validateLength = (value, minLength = 3) => {
    if (value.length < minLength) {
      return `Minimum ${minLength} characters required.`;
    }
    return null;
  };

  const validateUrl = (url) => {
    const regex = /^https:\/\/[a-zA-Z0-9-\.]+\.[a-zA-Z]{2,}$/;
    return regex.test(url) ? null : "Please enter a valid URL starting with https://";
  };

  const validateEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email) ? null : "Please enter a valid email address.";
  };

  const validatePhone = (phone) => {
    const regex = /^[0-9]{10}$/;
    return regex.test(phone) ? null : "Please enter a valid 10-digit phone number.";
  };

  const validatePasswordComplexity = (password) => {
    const regex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password)
      ? null
      : "Password must be at least 8 characters long, with at least one letter, one number, and one special character.";
  };
  const handleToggle = () => {
    if (type === 'password') {
      setIcon(eye);
      setType('text');
    } else {
      setIcon(eyeOff)
      setType('password')
    }
  }
  const handleConfirmToggle = () => {
    if (confirmType === 'password') {
      setConfirmIcon(eye);
      setConfirmType('text');
    } else {
      setConfirmIcon(eyeOff);
      setConfirmType('password');
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const emailError = validateEmail(formData.email);
    const passwordError = formData.password;
    if (emailError) {
      setError(emailError);
      setLoading(false);
      return;
    }

    try {
      console.log('posting to : ', "user/login")
      const response = await apiService.post("user/login", {
        email: formData.email,
        password: formData.password,
        loginType: loginType,
      }, "json", setErrorMessage, navigate);

      // Handle employee login response
      if (response.employee) {
        // Employee login successful
        localStorage.setItem("token", response.token);
        localStorage.setItem("employeeData", JSON.stringify(response.employee));
        localStorage.setItem("loginType", "employee");
        
        navigate("/employee-dashboard", { replace: true });
        onClose();
        return;
      }

      // Handle regular user login response
      const { token, user, enterpriseId, role } = response;
      login(user, token, enterpriseId, role, loginType);
      localStorage.setItem("loginType", loginType);
      // navigate(user.role === "applicant" ? "/applicant" : "/dashboard", { replace: true });
      let redirectPath = "/dashboard"; // Default to /dashboard for enterprise users

      if (user.role === "applicant" || user.role === "job-seeker") {
        redirectPath = "/applicant"; // Redirect to /applicant for job-seekers
      }

      navigate(redirectPath, { replace: true });
      window.location.reload(); // Ensure that the page reloads after redirection
      onClose(); // Close the modal on successful login
    } catch (error) {
      console.error("Login error:", error);
      handleError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!validatePasswordMatch()) return;

    const emailError = validateEmail(formData.email);
    const passwordError = validatePasswordComplexity(formData.password);
    const enterpriseWebsiteError =
      loginType === "employer" ? validateUrl(formData.enterpriseWebsite) : null;
    const phoneError = loginType === "employer" ? validatePhone(formData.enterpriseContact) : null;

    if (emailError || passwordError || enterpriseWebsiteError || phoneError) {
      setError(emailError || passwordError || enterpriseWebsiteError || phoneError);
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.post("user/register", {
        firstName: formData.firstname,
        lastName: formData.lastname,
        email: formData.email,
        password: formData.password,
        loginType: loginType,
        ...(loginType === "employer" && {
          enterpriseName: formData.enterpriseName,
          enterpriseDescription: formData.enterpriseDescription,
          enterpriseLocation: formData.enterpriseLocation,
          enterpriseWebsite: formData.enterpriseWebsite,
          enterpriseContact: formData.enterpriseContact,
        }),
      });

      alert(response.message || "Account created successfully. Please log in.");
      setView("login");
    } catch (error) {
      console.error("🔥 Signup error:", error);

      // Explicitly log the full response structure
      if (error.response) {
        console.debug("📌 Full error response:", error.response);
        console.debug("📌 Error response data:", error.response.data);
      }

      // Extract the error message properly
      let errorMessage = error.response?.data?.message || error.message || "An error occurred. Please try again.";

      console.log("🔥 Extracted error message:", errorMessage);
      setError(errorMessage);
    }

    finally {
      setLoading(false);
    }


  };


  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.post("user/forgot-password", {
        email: formData.email,
        loginType: loginType,
      }, "json", setErrorMessage);

      alert(response.message || "OTP sent to your email. Please enter it below.");
      setView("otp");
    } catch (error) {
      handleError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.post("user/verify-otp", {
        email: formData.email,
        otp: formData.otp,
        newPassword: formData.newPassword,
        loginType: loginType,
      }, "json", setErrorMessage);

      alert(response.message || "Password reset successfully. Please log in.");
      setView("login");
    } catch (error) {
      handleError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      {/*Size of the window(login, signup for both login's)*/}
      <div className="popup-container"
        style={{ width: "800px", maxWidth: "95%", maxHeight: "90vh", overflowY: "auto", }}>
        <button className="close-btn" onClick={onClose}>
          &times;
        </button>

        {error && <div className="error-message">{error}</div>}

        {view === "login" && (
          <div className="login-form">
            <h2>
              {loginType === "employer"
                ? "Management Login"
                : loginType === "employee"
                ? "Employee Login"
                : "Job Seeker Login"}
            </h2>
            <form onSubmit={handleLogin}>
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="input-field"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={type}
                  name="password"
                  placeholder="Password"
                  style={{
                    width: "100%",
                    padding: "10px 35px 10px 10px", // space for icon on the right
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxSizing: "border-box"
                  }}
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <span
                  onClick={handleToggle}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#555"
                  }}
                >
                  <Icon icon={icon} size={15} />
                </span>
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Logging in..." : "Login"}
              </button>
              <div className="popup-footer">
                <span onClick={() => setView("forgot")}>Forgot Password?</span> |{" "}
                <span onClick={() => setView("signup")}>
                  {loginType === "job-seeker"
                    ? "Create an Account as Job Seeker"
                    : loginType === "employer"
                    ? "Create an Account as Management"
                    : null}
                </span> |{" "}
                <span onClick={() => {
                  if (loginType === "job-seeker") setLoginType("employer");
                  else if (loginType === "employer") setLoginType("employee");
                  else setLoginType("job-seeker");
                }}>
                  <p>
                    {loginType === "job-seeker"
                      ? "Switch to Management Login"
                      : loginType === "employer"
                      ? "Switch to Employee Login"
                      : "Switch to Job Seeker Login"}
                  </p>
                </span>
              </div>
            </form>
          </div>
        )}

        {view === "signup" && (
          <div className="signup-form">
            <h2>
              {loginType === "job-seeker"
                ? "Create a Job Seeker Account"
                : loginType === "employer"
                ? "Create a Management Account"
                : null}
            </h2>
            <form onSubmit={handleSignup}>
              <input
                type="text"
                name="firstname"
                placeholder="First Name"
                className="input-field"
                value={formData.firstname}
                onChange={handleChange}
                required
              />
              <input
                type="text"
                name="lastname"
                placeholder="Last Name"
                className="input-field"
                value={formData.lastname}
                onChange={handleChange}
                required
              />
              {loginType === "employer" && (
                <div>
                  {/* You can add employee-specific signup fields here if needed */}
                  <input
                    type="text"
                    name="enterpriseName"
                    placeholder="Business Name"
                    className="input-field"
                    value={formData.enterpriseName}
                    onChange={handleChange}
                    required
                  />
                  <input
                    type="text"
                    name="enterpriseDescription"
                    placeholder="Business Description"
                    className="input-field"
                    value={formData.enterpriseDescription}
                    onChange={handleChange}
                    required
                  />
                  <input
                    type="text"
                    name="enterpriseLocation"
                    placeholder="Business Location"
                    className="input-field"
                    value={formData.enterpriseLocation}
                    onChange={handleChange}
                    required
                  />
                  <input
                    type="url"
                    name="enterpriseWebsite"
                    placeholder="Business Website"
                    className="input-field"
                    value={formData.enterpriseWebsite}
                    onChange={handleChange}
                    required
                  />
                  <input
                    type="tel"
                    name="enterpriseContact"
                    placeholder="Business Contact"
                    className="input-field"
                    value={formData.enterpriseContact}
                    onChange={handleChange}
                    pattern="[0-9]{10}"
                    required
                  />
                </div>
              )}
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="input-field"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={type}
                  name="password"
                  placeholder="Password"
                  style={{
                    width: "100%",
                    padding: "10px 35px 10px 10px", // space for icon on the right
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxSizing: "border-box"
                  }}
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <span
                  onClick={handleToggle}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#555"
                  }}
                >
                  <Icon icon={icon} size={15} />
                </span>
              </div>
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  type={confirmType}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  style={{
                    width: "100%",
                    padding: "10px 35px 10px 10px", // space for icon on the right
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxSizing: "border-box"
                  }}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <span
                  onClick={handleConfirmToggle}
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#555"
                  }}
                >
                  <Icon icon={confirmIcon} size={15} />
                </span>
              </div>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Signing up..." : "Sign Up"}
              </button>
              <div className="popup-footer">
                <span onClick={() => { setView("login"); setError(null); }}>Already have an account? Login</span>
              </div>
            </form>
          </div>
        )}

        {view === "forgot" && (
          <div className="forgot-form">
            <h2>Forgot Password</h2>
            <form onSubmit={handleForgotPassword}>
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="input-field"
                value={formData.email}
                onChange={handleChange}
                required
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Sending..." : "Reset Password"}
              </button>
              <div className="popup-footer">
                <span onClick={() => setView("login")}>Back to Login</span>
              </div>
            </form>
          </div>
        )}

        {view === "otp" && (
          <div className="otp-form">
            <h2>Verify OTP</h2>
            <form onSubmit={handleVerifyOTP}>
              <input
                type="text"
                name="otp"
                placeholder="Enter OTP"
                className="input-field"
                value={formData.otp}
                onChange={handleChange}
                required
              />
              <input
                type="password"
                name="newPassword"
                placeholder="Enter New Password"
                className="input-field"
                value={formData.newPassword}
                onChange={handleChange}
                required
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Verifying..." : "Reset Password"}
              </button>
              <div className="popup-footer">
                <span onClick={() => setView("forgot")}>Back to Forgot Password</span>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginModal;